// ANGEL FLIGHT — CrewComms: the crew-chatter director (G2's feel carrier).
//
// Listens to gameplay bus events and turns them into authored radio lines with
// radio discipline: one global gap between ANY two lines plus a per-category
// cooldown, so the crew comments on the battle instead of narrating every
// round. Lines cycle round-robin per category (never the same phrase twice in
// a row, fully deterministic — no RNG). Routine lines that can't clear the gap
// are DROPPED (a kill confirm 2s late is noise); urgent lines (check-fire,
// hull critical, mission beats) queue and fire the moment the gap clears.
//
// Output is a single bus event — `comms:line` — consumed by the HudOverlay
// ticker and the AudioBus squelch blip. Lifecycle mirrors AudioBus/HudOverlay:
// bind(bus)/unbind(); App drives update(dtMs) from its rAF tick. Render-side:
// never touches ctx.rng or sim state.

import { COMMS } from '../config/comms';
import type { CommsCategory, CommsCategoryId } from '../config/comms';
import type { TypedEmitter } from '../core/EventBus';

interface CategoryState {
  nextIdx: number;
  lastAtMs: number;
}

export class CrewComms {
  private bus: TypedEmitter | null = null;
  private nowMs = 0;
  private lastLineAtMs = -Infinity;
  private readonly state = new Map<CommsCategoryId, CategoryState>();
  /** At most one queued urgent line (the newest urgent request wins). */
  private pending: CommsCategoryId | null = null;
  private prevHull = Infinity;
  private resolved = false;

  bind(bus: TypedEmitter): void {
    this.unbind();
    this.bus = bus;
    this.nowMs = 0;
    this.lastLineAtMs = -Infinity;
    this.pending = null;
    this.prevHull = Infinity;
    this.resolved = false;
    this.state.clear();

    bus.on('enemy:killed', this.onKilled);
    bus.on('combo:changed', this.onCombo);
    bus.on('wave:banner', this.onWave);
    bus.on('enemy:telegraph', this.onTelegraph);
    bus.on('hull:damaged', this.onHull);
    bus.on('friendly:hit', this.onFriendlyHit);
    bus.on('friendly:warning', this.onFriendlyWarning);
    bus.on('friendly:down', this.onFriendlyDown);
    bus.on('weapon:dry', this.onDry);
    bus.on('mission:cleared', this.onCleared);
    bus.on('mission:failed', this.onFailed);
  }

  unbind(): void {
    if (!this.bus) return;
    this.bus.off('enemy:killed', this.onKilled);
    this.bus.off('combo:changed', this.onCombo);
    this.bus.off('wave:banner', this.onWave);
    this.bus.off('enemy:telegraph', this.onTelegraph);
    this.bus.off('hull:damaged', this.onHull);
    this.bus.off('friendly:hit', this.onFriendlyHit);
    this.bus.off('friendly:warning', this.onFriendlyWarning);
    this.bus.off('friendly:down', this.onFriendlyDown);
    this.bus.off('weapon:dry', this.onDry);
    this.bus.off('mission:cleared', this.onCleared);
    this.bus.off('mission:failed', this.onFailed);
    this.bus = null;
  }

  /** Advance the comms clock; flush a queued urgent line once the gap clears. */
  update(dtMs: number): void {
    this.nowMs += dtMs;
    if (this.pending && this.nowMs - this.lastLineAtMs >= COMMS.globalGapMs) {
      const cat = this.pending;
      this.pending = null;
      this.say(cat, true);
    }
  }

  // ── event handlers (arrow props → stable identity for off()) ──────────────

  private readonly onKilled = (p: { headshot: boolean }): void => {
    if (this.resolved) return;
    this.request(p.headshot ? 'headshot' : 'killConfirm');
  };

  private readonly onCombo = (p: { count: number }): void => {
    if (this.resolved) return;
    if (p.count > 0 && p.count % 10 === 0) this.request('streak');
  };

  private readonly onWave = (): void => {
    if (!this.resolved) this.request('waveContact');
  };

  private readonly onTelegraph = (): void => {
    if (!this.resolved) this.request('telegraph');
  };

  private readonly onHull = (p: { hull: number; maxHull: number }): void => {
    if (this.resolved) return;
    const dropped = p.hull < this.prevHull;
    this.prevHull = p.hull;
    if (!dropped) return;
    if (p.hull / p.maxHull <= 0.3) this.request('hullCritical');
    else this.request('hullDamage');
  };

  private readonly onFriendlyHit = (): void => {
    if (!this.resolved) this.request('friendlyHit');
  };

  private readonly onFriendlyWarning = (): void => {
    if (!this.resolved) this.request('friendlyWarning');
  };

  private readonly onFriendlyDown = (): void => {
    if (!this.resolved) this.request('friendlyDown');
  };

  private readonly onDry = (): void => {
    if (!this.resolved) this.request('dry');
  };

  // Mission beats end the chatter: say the verdict line, then go quiet.
  private readonly onCleared = (): void => {
    this.request('cleared');
    this.resolved = true;
  };

  private readonly onFailed = (): void => {
    this.request('failed');
    this.resolved = true;
  };

  // ── director core ──────────────────────────────────────────────────────────

  private request(cat: CommsCategoryId): void {
    const def: CommsCategory = COMMS.categories[cat];
    const st = this.state.get(cat);
    // Per-category cooldown: too soon after the same kind of line → drop.
    if (st && this.nowMs - st.lastAtMs < def.cooldownMs) return;

    if (this.nowMs - this.lastLineAtMs < COMMS.globalGapMs) {
      // Radio is busy. Urgent lines wait their turn; routine ones drop.
      if (def.urgent === true) this.pending = cat;
      return;
    }
    this.say(cat, def.urgent === true);
  }

  private say(cat: CommsCategoryId, urgent: boolean): void {
    const bus = this.bus;
    if (!bus) return;
    const def: CommsCategory = COMMS.categories[cat];
    let st = this.state.get(cat);
    if (!st) {
      st = { nextIdx: 0, lastAtMs: -Infinity };
      this.state.set(cat, st);
    }
    const text = def.lines[st.nextIdx % def.lines.length];
    if (text === undefined) return;
    st.nextIdx = (st.nextIdx + 1) % def.lines.length;
    st.lastAtMs = this.nowMs;
    this.lastLineAtMs = this.nowMs;
    bus.emit('comms:line', { text, speaker: COMMS.speaker, urgent });
  }

  destroy(): void {
    this.unbind();
  }
}
